// src/components/ecommerce/CategoryNavigation.jsx - Updated with subcategory support

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Grid,
  Package,
  Shirt,
  Smartphone,
  Home,
  Book,
  Gamepad2,
  Car,
  Heart,
  Baby,
  Zap,
  Coffee,
  Dumbbell,
  Palette,
  Music,
  Camera,
  Plane,
  Gift,
  Star,
  Tag,
  TrendingUp,
  Clock,
  Filter,
  Folder
} from 'lucide-react';

const CATEGORY_ICONS = {
  'electronics': Smartphone,
  'clothing': Shirt,
  'home-garden': Home,
  'books': Book,
  'sports': Dumbbell,
  'toys-games': Gamepad2,
  'automotive': Car,
  'health-beauty': Heart,
  'baby-kids': Baby,
  'digital': Zap,
  'food-beverages': Coffee,
  'art-crafts': Palette,
  'music': Music,
  'photography': Camera,
  'travel': Plane,
  'gifts': Gift,
  'jewelry': Star,
  'sale': Tag,
  'trending': TrendingUp,
  'new': Clock,
  'featured': Star
};

export const CategoryNavigation = ({ 
  bookData,
  categories = [],
  selectedCategory = 'all',
  selectedSubcategory = 'all', // NEW
  onCategorySelect,
  onSubcategorySelect, // NEW
  styling = {},
  showProductCount = true,
  layout = 'horizontal', // horizontal, vertical, grid
  showSubcategories = true, // NEW
  className = '',
  layoutSettings = {},
  headerHeight = 80
}) => {
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef(null);
  const subScrollContainerRef = useRef(null); // NEW

  const design = bookData.design;
  const storeSettings = bookData.storeSettings;
  const primaryColor = design.primaryColor || '#3b82f6';
  const accentColor = design.accentColor || '#f59e0b';
  const cardRadius = design.cardRadius || '12px';
  const shadowLevel = design.shadowLevel || 'medium';
  const imageFit = design.imageFit || 'cover';
  const imageAspectRatio = design.imageAspectRatio || '1:1';
  const fontFamily = design.fontFamily || 'Inter';
  const gridColumns = design.gridColumns || 3; // For ProductGrid

  const isCategoriesVisible = layoutSettings?.sectionVisibility?.categories !== false;

  const getStickyTopPosition = () => {
    const isOverlayMode = layoutSettings?.headerHeroIntegration === 'overlay';
    const isHeroVisible = layoutSettings?.sectionVisibility?.hero !== false;
    
    // If header is in overlay mode and hero is visible, categories start from top
    if (isOverlayMode && isHeroVisible) {
      return 0;
    }
    
    // Otherwise, position below the header
    return headerHeight;
  };

  const stickyTopPosition = getStickyTopPosition();
  

  // NEW: Get available subcategories for selected category
  const availableSubcategories = useMemo(() => {
    if (!isCategoriesVisible) {
      return null;
    }
    if (selectedCategory === 'all') return [];
    const category = categories.find(cat => cat.id === selectedCategory);
    return category?.subcategories || [];
  }, [selectedCategory, categories]);

  useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, [categories]);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const hasOverflow = container.scrollWidth > container.clientWidth;
    setShowScrollButtons(hasOverflow);
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth);
  };

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
      setTimeout(checkScrollButtons, 300);
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
      setTimeout(checkScrollButtons, 300);
    }
  };

  // NEW: Subcategory scroll functions
  const scrollSubLeft = () => {
    const container = subScrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollSubRight = () => {
    const container = subScrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  const getCategoryIcon = (categoryId) => {
    const IconComponent = CATEGORY_ICONS[categoryId] || Package;
    return IconComponent;
  };

  const allCategory = {
    id: 'all',
    name: 'All Products',
    productCount: categories.reduce((sum, cat) => sum + (cat.productCount || 0), 0),
    icon: Grid
  };

  const allCategories = [allCategory, ...categories];

  if (layout === 'grid') {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Shop by Category</h2>
          <button
            onClick={() => onCategorySelect?.('all')}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <Grid className="h-4 w-4" />
            View All
          </button>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {allCategories.map(category => {
            const IconComponent = category.icon || getCategoryIcon(category.id);
            const isSelected = selectedCategory === category.id;
            
            return (
              <button
              key={category.id}
              onClick={() => onCategorySelect?.(category.id)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                isSelected
                  ? 'bg-blue-50 shadow-md' // keep neutral bg
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
              style={{
                borderColor: isSelected ? primaryColor : undefined,
              }}
            >
              <div className="flex flex-col items-center text-center">
                <div
                  className={`p-3 rounded-full mb-3 ${
                    isSelected ? '' : 'bg-gray-100'
                  }`}
                  style={{
                    backgroundColor: isSelected ? `${primaryColor}20` : undefined, // light tint
                  }}
                >
                  <IconComponent
                    className="h-6 w-6"
                    style={{ color: isSelected ? primaryColor : '#4b5563' }}
                  />
                </div>
                <span
                  className="font-medium text-sm mb-1"
                  style={{ color: isSelected ? primaryColor : '#111827' }}
                >
                  {category.name}
                </span>

                {showProductCount && category.productCount !== undefined && (
                  <span className="text-xs text-gray-500">
                    {category.productCount} items
                  </span>
                )}

                {category.id !== 'all' && category.subcategories && category.subcategories.length > 0 && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                    <Folder className="h-3 w-3" />
                    <span>{category.subcategories.length}</span>
                  </div>
                )}
              </div>
            </button>
            );
          })}
        </div>

        {/* NEW: Subcategories Grid */}
        {showSubcategories && selectedCategory !== 'all' && availableSubcategories.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {categories.find(c => c.id === selectedCategory)?.name} Categories
              </h3>
              <button
                onClick={() => onSubcategorySelect?.('all')}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                View All
              </button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {availableSubcategories
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(subcategory => {
                  const isSelected = selectedSubcategory === subcategory.id;
                  
                  return (
                    <button
                      key={subcategory.id}
                      onClick={() => onSubcategorySelect?.(subcategory.id)}
                      className={`p-3 rounded-lg border transition-all duration-200 text-left ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Folder className={`h-4 w-4 flex-shrink-0 ${
                          isSelected ? 'text-blue-600' : 'text-gray-400'
                        }`} />
                        <span className={`font-medium text-sm ${
                          isSelected ? 'text-blue-600' : 'text-gray-900'
                        }`}>
                          {subcategory.name}
                        </span>
                      </div>
                    </button>
                  );
                })
              }
            </div>
          </div>
        )}
      </div>
    );
  }

  if (layout === 'vertical') {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
        <div className="p-4 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Categories
          </h3>
        </div>
        
        <div className="p-2">
          {allCategories.map(category => {
            const IconComponent = category.icon || getCategoryIcon(category.id);
            const isSelected = selectedCategory === category.id;
            
            return (
              <div key={category.id}>
                <button
                  onClick={() => onCategorySelect?.(category.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isSelected
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <IconComponent className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 text-left font-medium">{category.name}</span>
                  {showProductCount && category.productCount !== undefined && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {category.productCount}
                    </span>
                  )}
                  {/* NEW: Show subcategory chevron */}
                  {category.id !== 'all' && category.subcategories && category.subcategories.length > 0 && (
                    <ChevronDown className={`h-4 w-4 transition-transform ${
                      isSelected ? 'rotate-180' : ''
                    }`} />
                  )}
                </button>

                {/* NEW: Vertical subcategories */}
                {showSubcategories && isSelected && availableSubcategories.length > 0 && (
                  <div className="ml-7 mt-2 space-y-1 pb-2">
                    <button
                      onClick={() => onSubcategorySelect?.('all')}
                      className={`w-full flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                        selectedSubcategory === 'all'
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Grid className="h-3 w-3" />
                      All {category.name}
                    </button>
                    
                    {availableSubcategories
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map(subcategory => {
                        const isSubSelected = selectedSubcategory === subcategory.id;
                        
                        return (
                          <button
                            key={subcategory.id}
                            onClick={() => onSubcategorySelect?.(subcategory.id)}
                            className={`w-full flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                              isSubSelected
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <Folder className="h-3 w-3" />
                            {subcategory.name}
                          </button>
                        );
                      })
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Horizontal layout (default) with subcategories
  return (
    <div 
      className={`bg-white border-b z-30 ${className}`}
      style={{ 
        position: 'sticky',
        top: `${stickyTopPosition}px`
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Categories */}
        <div className="relative">
          {/* Scroll Left Button */}
          {showScrollButtons && canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white shadow-lg rounded-full border border-gray-200 hover:bg-gray-50"
              style={{ marginLeft: '-1rem' }}
            >
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>
          )}

          {/* Categories Container */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto scrollbar-hide py-4 gap-2"
            onScroll={checkScrollButtons}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {allCategories.map(category => {
              const IconComponent = category.icon || getCategoryIcon(category.id);
              const isSelected = selectedCategory === category.id;
              
              return (
                <button
                  key={category.id}
                  onClick={() => onCategorySelect?.(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all duration-200 border-2 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-600 shadow-sm'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <IconComponent className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium">{category.name}</span>
                  {showProductCount && category.productCount !== undefined && (
                    <span 
                      className={`text-xs px-2 py-1 rounded-full ${
                        isSelected 
                          ? 'bg-blue-200 text-blue-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {category.productCount}
                    </span>
                  )}
                  {/* NEW: Subcategory indicator */}
                  {category.id !== 'all' && category.subcategories && category.subcategories.length > 0 && (
                    <span className="text-xs text-gray-400">•{category.subcategories.length}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Scroll Right Button */}
          {showScrollButtons && canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white shadow-lg rounded-full border border-gray-200 hover:bg-gray-50"
              style={{ marginRight: '-1rem' }}
            >
              <ChevronRight className="h-4 w-4 text-gray-600" />
            </button>
          )}
        </div>

        {/* NEW: Subcategories Horizontal Navigation */}
        {showSubcategories && selectedCategory !== 'all' && availableSubcategories.length > 0 && (
          <div className="border-t border-gray-100 relative">
            <div
              ref={subScrollContainerRef}
              className="flex overflow-x-auto scrollbar-hide py-3 gap-2"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <button
                onClick={() => onSubcategorySelect?.('all')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full whitespace-nowrap text-sm font-medium transition-all duration-200 ${
                  selectedSubcategory === 'all'
                    ? 'bg-blue-100 text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Grid className="h-3 w-3" />
                All {categories.find(c => c.id === selectedCategory)?.name}
              </button>
              
              {availableSubcategories
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(subcategory => {
                  const isSubSelected = selectedSubcategory === subcategory.id;
                  
                  return (
                    <button
                      key={subcategory.id}
                      onClick={() => onSubcategorySelect?.(subcategory.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full whitespace-nowrap text-sm font-medium transition-all duration-200 ${
                        isSubSelected
                          ? 'bg-blue-100 text-blue-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Folder className="h-3 w-3" />
                      {subcategory.name}
                    </button>
                  );
                })
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};